"use client";

import { CopyField } from "@commerce/components/CopyField";
import { SURFACE } from "@commerce/components/page";
import { Time } from "@commerce/components/time";
import { AssistantParts } from "@commerce/conversations/Transcript";
import type { TranscriptMessage } from "@commerce/conversations/types";
import { formatMoney } from "@commerce/shopper/types";
import { useConfirmationAlert } from "@saas/shared/components/ConfirmationAlertProvider";
import { orpc } from "@shared/lib/orpc";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { getRouteApi } from "@tanstack/react-router";
import { Badge } from "@ui/components/badge";
import { Button } from "@ui/components/button";
import {
	Empty,
	EmptyDescription,
	EmptyHeader,
	EmptyMedia,
	EmptyTitle,
} from "@ui/components/empty";
import { LinkButton } from "@ui/components/link-button";
import { Skeleton } from "@ui/components/skeleton";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@ui/components/tabs";
import { toast } from "@ui/components/toast";
import { cn } from "@ui/lib";
import { isTextUIPart, isToolUIPart, type UIMessage } from "ai";
import {
	ArrowLeftIcon,
	ExternalLinkIcon,
	MessagesSquareIcon,
	PackageIcon,
	PhoneIcon,
	PhoneOffIcon,
	ShoppingBasketIcon,
} from "lucide-react";
import {
	type RefObject,
	useCallback,
	useEffect,
	useMemo,
	useRef,
	useState,
} from "react";
import { CallPlayer, type CallPlayerHandle } from "./CallPlayer";
import {
	activeIndex,
	CALL_STATUS_META,
	callerLabel,
	clockDuration,
	END_REASON_LABEL,
	EVENT_LABEL,
	isLive,
	offsetOf,
	usdCost,
	withDeltas,
} from "./meta";
import type { CallDetail as Detail } from "./types";

const route = getRouteApi("/_saas/app/_org/$organizationSlug/calls/");

/** Messages a little after the hang-up still belong to the call (the last reply is saved late). */
const AFTER_CALL_MS = 30_000;

/**
 * One call: who, how long, the recording with the transcript beside it, what was sent to the
 * chat, and the technical trail (spec 5.4).
 */
export function CallDetail({
	call,
	organizationId,
	organizationSlug,
	onBack,
}: {
	call: Detail;
	organizationId: string;
	organizationSlug: string;
	onBack: () => void;
}) {
	const search = route.useSearch();
	const navigate = route.useNavigate();
	const tab = search.tab ?? "conversation";
	const player = useRef<CallPlayerHandle>(null);
	const [playingMs, setPlayingMs] = useState(0);
	const onTime = useCallback((ms: number) => setPlayingMs(ms), []);
	const live = isLive(call.status);

	const conversation = useQuery({
		...orpc.conversations.get.queryOptions({
			input: { conversationId: call.conversationId ?? "" },
		}),
		enabled: Boolean(call.conversationId),
	});
	const messages = useMemo(
		() => callMessages(conversation.data?.messages ?? [], call),
		[conversation.data?.messages, call],
	);

	return (
		<div className="flex min-h-0 flex-1 flex-col">
			<CallHeader
				call={call}
				organizationId={organizationId}
				playerRef={player}
				onTime={onTime}
				onBack={onBack}
			/>

			<Tabs
				value={tab}
				onValueChange={(next) =>
					navigate({
						search: (prev) => ({
							...prev,
							tab:
								next === "conversation"
									? undefined
									: (next as "products" | "technical"),
						}),
						replace: true,
					})
				}
				className="min-h-0 flex-1 gap-0"
			>
				<div className="flex shrink-0 items-center justify-between gap-2 border-border/70 border-b px-4 py-2 md:px-6">
					<TabsList>
						<TabsTrigger value="conversation">
							Conversation
						</TabsTrigger>
						<TabsTrigger value="products">Products</TabsTrigger>
						<TabsTrigger value="technical">Technical</TabsTrigger>
					</TabsList>
					{call.conversationId ? (
						<LinkButton
							variant="ghost"
							size="sm"
							to="/app/$organizationSlug/conversations"
							params={{ organizationSlug }}
							search={{ id: call.conversationId }}
							className="max-sm:px-2"
						>
							<MessagesSquareIcon data-icon="inline-start" />
							<span className="max-sm:sr-only">Open chat</span>
						</LinkButton>
					) : null}
				</div>
				<div className="min-h-0 flex-1 overflow-y-auto">
					<div className="mx-auto flex max-w-3xl flex-col gap-5 px-4 py-5 md:px-6">
						<TabsContent
							value="conversation"
							className="flex flex-col gap-5"
						>
							<CallSummary
								call={call}
								basket={conversation.data?.basket ?? null}
								organizationSlug={organizationSlug}
							/>
							{live ? <LiveCaptions callId={call.id} /> : null}
							<CallTranscript
								call={call}
								loading={
									conversation.isPending &&
									Boolean(call.conversationId)
								}
								messages={messages}
								playingMs={call.hasRecording ? playingMs : null}
								onSeek={
									call.hasRecording && !live
										? (ms) => player.current?.seek(ms)
										: null
								}
							/>
						</TabsContent>
						<TabsContent value="products">
							<CallProducts
								messages={messages}
								loading={
									conversation.isPending &&
									Boolean(call.conversationId)
								}
							/>
						</TabsContent>
						<TabsContent value="technical">
							<Technical
								call={call}
								organizationSlug={organizationSlug}
							/>
						</TabsContent>
					</div>
				</div>
			</Tabs>
		</div>
	);
}

/** Who called, when and for how long, the hang-up button while live, the recording after. */
function CallHeader({
	call,
	organizationId,
	playerRef,
	onTime,
	onBack,
}: {
	call: Detail;
	organizationId: string;
	playerRef: RefObject<CallPlayerHandle | null>;
	onTime: (ms: number) => void;
	onBack: () => void;
}) {
	const live = isLive(call.status);
	const status = CALL_STATUS_META[call.status];
	return (
		<header className="flex shrink-0 flex-col gap-3 border-border/70 border-b px-4 py-3 md:px-6">
			<div className="flex items-start gap-2">
				<Button
					variant="ghost"
					size="icon-sm"
					onClick={onBack}
					aria-label="Back to calls"
					className="-ml-1.5 mt-0.5 lg:hidden"
				>
					<ArrowLeftIcon />
				</Button>
				<div className="flex min-w-0 flex-1 flex-col gap-1">
					<h2 className="flex flex-wrap items-center gap-2 font-semibold text-lg tracking-tight">
						<span dir="auto">{callerLabel(call)}</span>
						<Badge variant={status.variant}>
							{call.status === "LIVE" ? (
								<span
									aria-hidden
									className="size-1.5 rounded-full bg-current"
								/>
							) : null}
							{status.label}
						</Badge>
					</h2>
					<CallFacts call={call} />
				</div>
				{live ? (
					<EndCallButton
						call={call}
						organizationId={organizationId}
					/>
				) : null}
			</div>
			{call.hasRecording && !live ? (
				<CallPlayer
					ref={playerRef}
					organizationId={organizationId}
					callId={call.id}
					durationSeconds={
						call.recordingSeconds ?? call.durationSeconds ?? 0
					}
					onTime={onTime}
				/>
			) : null}
		</header>
	);
}

function CallFacts({ call }: { call: Detail }) {
	return (
		<p className="flex flex-wrap items-center gap-x-1.5 text-muted-foreground text-sm">
			{call.callerName && call.callerPhone ? (
				<span className="font-mono text-xs">{call.callerPhone} ·</span>
			) : null}
			<Time at={new Date(call.ringingAt)} label="Rang" />
			{call.durationSeconds !== null ? (
				<span className="tabular">
					· {clockDuration(call.durationSeconds)}
				</span>
			) : null}
			{call.endReason ? (
				<span>
					· {END_REASON_LABEL[call.endReason] ?? call.endReason}
				</span>
			) : null}
		</p>
	);
}

/** The call's own turns: spoken (input CALL) between the answer and shortly after the hang-up. */
function callMessages(messages: readonly TranscriptMessage[], call: Detail) {
	const start = new Date(call.answeredAt ?? call.ringingAt).getTime() - 5_000;
	const end = call.endedAt
		? new Date(call.endedAt).getTime() + AFTER_CALL_MS
		: Number.POSITIVE_INFINITY;
	return messages.filter((m) => {
		const stored = (m as { callId?: string | null }).callId;
		if (stored !== undefined && stored !== null) {
			return stored === call.id;
		}
		const at = new Date(m.createdAt).getTime();
		return m.input === "CALL" && at >= start && at <= end;
	});
}

function CallSummary({
	call,
	basket,
	organizationSlug,
}: {
	call: Detail;
	basket: { itemCount: number; total: string; currency: string } | null;
	organizationSlug: string;
}) {
	if (!call.summary && !basket?.itemCount && !call.orderId) {
		return null;
	}
	return (
		<div className={cn(SURFACE, "flex flex-col gap-2 p-4 text-sm")}>
			{call.summary ? (
				<p dir="auto" className="text-pretty text-left">
					{call.summary}
				</p>
			) : null}
			<div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-muted-foreground text-xs">
				{basket?.itemCount ? (
					<span className="inline-flex items-center gap-1 tabular">
						<ShoppingBasketIcon className="size-3.5" aria-hidden />
						Basket {basket.itemCount} item
						{basket.itemCount === 1 ? "" : "s"} ·{" "}
						{formatMoney(basket.total, basket.currency)}
					</span>
				) : null}
				{call.orderId ? (
					<LinkButton
						variant="link"
						size="xs"
						to="/app/$organizationSlug/orders"
						params={{ organizationSlug }}
						search={{ id: call.orderId }}
						className="h-auto px-0"
					>
						<PackageIcon data-icon="inline-start" />
						Order placed on this call
					</LinkButton>
				) : null}
			</div>
		</div>
	);
}

function CallTranscript({
	call,
	loading,
	messages,
	playingMs,
	onSeek,
}: {
	call: Detail;
	loading: boolean;
	messages: TranscriptMessage[];
	playingMs: number | null;
	onSeek: ((ms: number) => void) | null;
}) {
	const offsets = messages.map((m) =>
		offsetOf(m as Parameters<typeof offsetOf>[0], call.answeredAt),
	);
	const active = playingMs === null ? -1 : activeIndex(offsets, playingMs);
	const activeRef = useRef<HTMLLIElement | null>(null);
	useEffect(() => {
		if (active >= 0) {
			activeRef.current?.scrollIntoView({
				block: "nearest",
				behavior: "smooth",
			});
		}
	}, [active]);

	if (loading) {
		return (
			<div className="flex flex-col gap-4">
				{[0, 1, 2].map((i) => (
					<div key={i} className="flex gap-3">
						<Skeleton className="h-4 w-10" />
						<div className="flex flex-1 flex-col gap-1.5">
							<Skeleton className="h-4 w-16" />
							<Skeleton className="h-4 w-full" />
						</div>
					</div>
				))}
			</div>
		);
	}
	if (messages.length === 0) {
		return (
			<Empty className="rounded-3xl border py-8">
				<EmptyHeader>
					<EmptyMedia variant="icon">
						{call.answeredAt ? <PhoneIcon /> : <PhoneOffIcon />}
					</EmptyMedia>
					<EmptyTitle>
						{call.answeredAt ? "Nothing was said" : "Nobody spoke"}
					</EmptyTitle>
					<EmptyDescription>
						{call.answeredAt
							? "The caller hung up before the first turn."
							: "The call ended before the agent answered."}
					</EmptyDescription>
				</EmptyHeader>
			</Empty>
		);
	}
	return (
		<ol aria-label="Call transcript" className="flex flex-col gap-1">
			{messages.map((message, index) => {
				const offset = offsets[index] ?? null;
				const parts = (message.parts ?? []) as UIMessage["parts"];
				const shopper = message.role === "user";
				const isActive = index === active;
				return (
					<li
						key={message.id}
						ref={isActive ? activeRef : undefined}
						className={cn(
							"flex gap-3 rounded-2xl px-2 py-2 transition-colors duration-(--duration-hover)",
							isActive && "bg-muted",
						)}
					>
						{offset !== null && onSeek ? (
							<button
								type="button"
								onClick={() => onSeek(offset)}
								aria-label={`Play from ${clockDuration(offset / 1000)}`}
								className="mt-0.5 h-5 w-11 shrink-0 rounded-lg text-left font-mono text-muted-foreground text-xs tabular outline-none hover:text-foreground focus-visible:ring-3 focus-visible:ring-ring/30"
							>
								{clockDuration(offset / 1000)}
							</button>
						) : (
							<span className="mt-0.5 w-11 shrink-0 font-mono text-muted-foreground text-xs tabular">
								{offset !== null
									? clockDuration(offset / 1000)
									: ""}
							</span>
						)}
						<div className="flex min-w-0 flex-1 flex-col gap-1">
							<span className="font-medium text-muted-foreground text-xs">
								{shopper
									? callerLabel(call)
									: message.role === "assistant"
										? "Agent"
										: "Team"}
							</span>
							{shopper ? (
								<p
									dir="auto"
									className="text-pretty text-left text-sm"
								>
									{parts
										.filter(isTextUIPart)
										.map((p) => p.text)
										.join(" ")}
								</p>
							) : (
								<AssistantParts id={message.id} parts={parts} />
							)}
						</div>
					</li>
				);
			})}
		</ol>
	);
}

/** What the agent looked up and sent to the chat during the call. */
function CallProducts({
	messages,
	loading,
}: {
	messages: TranscriptMessage[];
	loading: boolean;
}) {
	if (loading) {
		return <Skeleton className="h-24 w-full rounded-3xl" />;
	}
	const tools = messages.flatMap((m) =>
		((m.parts ?? []) as UIMessage["parts"])
			.filter(isToolUIPart)
			.map((part, index) => ({ key: `${m.id}-${index}`, part })),
	);
	if (tools.length === 0) {
		return (
			<Empty className="rounded-3xl border py-8">
				<EmptyHeader>
					<EmptyMedia variant="icon">
						<PackageIcon />
					</EmptyMedia>
					<EmptyTitle>No products on this call</EmptyTitle>
					<EmptyDescription>
						Searches and cards sent to the chat show here.
					</EmptyDescription>
				</EmptyHeader>
			</Empty>
		);
	}
	return (
		<div className="flex flex-col gap-2">
			{tools.map(({ key, part }) => (
				<AssistantParts
					key={key}
					id={key}
					parts={[part] as UIMessage["parts"]}
				/>
			))}
		</div>
	);
}

interface Caption {
	key: string;
	speaker: "caller" | "agent";
	text: string;
}

/**
 * Live captions while the call is on (B4's `GET /api/calls/:id/events`, the call's own
 * messages). What was heard arrives whole; the agent's reply builds up as it is written.
 */
function LiveCaptions({ callId }: { callId: string }) {
	const [captions, setCaptions] = useState<Caption[]>([]);
	const [state, setState] = useState<"connecting" | "open" | "unavailable">(
		"connecting",
	);
	useEffect(() => {
		const source = new EventSource(
			`/api/calls/${encodeURIComponent(callId)}/events`,
		);
		source.onopen = () => setState("open");
		source.onerror = () => {
			if (source.readyState === EventSource.CLOSED) {
				setState("unavailable");
			}
		};
		source.onmessage = (event) => {
			let message: { type?: string; turn?: number; text?: string };
			try {
				message = JSON.parse(event.data);
			} catch {
				return;
			}
			const turn = message.turn ?? 0;
			if (message.type === "heard" && message.text) {
				const text = message.text;
				setCaptions((prev) => [
					...prev,
					{ key: `h-${turn}`, speaker: "caller", text },
				]);
			} else if (message.type === "reply-delta" && message.text) {
				const delta = message.text;
				setCaptions((prev) => {
					const key = `a-${turn}`;
					const last = prev.at(-1);
					return last?.key === key
						? [
								...prev.slice(0, -1),
								{ ...last, text: last.text + delta },
							]
						: [...prev, { key, speaker: "agent", text: delta }];
				});
			} else if (message.type === "reply-done" && message.text) {
				const text = message.text;
				setCaptions((prev) => {
					const key = `a-${turn}`;
					const rest = prev.filter((c) => c.key !== key);
					return [...rest, { key, speaker: "agent", text }];
				});
			}
		};
		return () => source.close();
	}, [callId]);

	return (
		<section
			aria-label="Live captions"
			aria-live="polite"
			className={cn(SURFACE, "flex flex-col gap-2 p-4")}
		>
			<span className="flex items-center gap-2 font-medium text-muted-foreground text-xs">
				<span
					aria-hidden
					className={cn(
						"size-1.5 rounded-full",
						state === "open"
							? "bg-success"
							: "bg-muted-foreground/50",
					)}
				/>
				{state === "unavailable"
					? "Live captions unavailable. The transcript appears when the call ends."
					: "Live captions"}
			</span>
			{captions.length === 0 && state !== "unavailable" ? (
				<p className="text-muted-foreground text-sm">
					Waiting for someone to speak…
				</p>
			) : (
				<ul className="flex flex-col gap-1.5 text-sm">
					{captions.slice(-12).map((caption) => (
						<li key={caption.key} className="flex gap-2">
							<span className="w-12 shrink-0 font-medium text-muted-foreground text-xs leading-5">
								{caption.speaker === "caller"
									? "Caller"
									: "Agent"}
							</span>
							<span
								dir="auto"
								className="min-w-0 flex-1 text-left"
							>
								{caption.text}
							</span>
						</li>
					))}
				</ul>
			)}
		</section>
	);
}

function EndCallButton({
	call,
	organizationId,
}: {
	call: Detail;
	organizationId: string;
}) {
	const { confirm } = useConfirmationAlert();
	const queryClient = useQueryClient();
	const end = useMutation(orpc.calls.end.mutationOptions());
	return (
		<Button
			variant="outline"
			size="sm"
			className="shrink-0 text-destructive"
			disabled={end.isPending}
			onClick={() =>
				confirm({
					title: `Hang up on ${callerLabel(call)}?`,
					message:
						"The call ends at once. The caller can still write in the chat.",
					confirmLabel: "Hang up",
					destructive: true,
					onConfirm: async () => {
						try {
							await end.mutateAsync({
								organizationId,
								callId: call.id,
							});
							toast.add({ type: "success", title: "Call ended" });
						} catch (error) {
							toast.add({
								type: "error",
								title: "Couldn't end the call",
								description:
									error instanceof Error
										? error.message
										: "Try again in a moment.",
							});
						}
						await queryClient.invalidateQueries({
							queryKey: orpc.calls.key(),
						});
					},
				})
			}
		>
			<PhoneOffIcon data-icon="inline-start" />
			Hang up
		</Button>
	);
}

function Technical({
	call,
	organizationSlug,
}: {
	call: Detail;
	organizationSlug: string;
}) {
	const terminate = [...call.events]
		.reverse()
		.find(
			(e) =>
				e.type === "terminate_webhook" || e.type === "terminate_sent",
		);
	const rows = listeningRows(call);

	return (
		<div className="flex flex-col gap-6">
			{call.errorCode !== null ? <MetaError call={call} /> : null}

			<CallIdentifiers call={call} organizationSlug={organizationSlug} />

			<CallTimeline events={call.events} />

			<section className="flex flex-col gap-3">
				<h3 className="font-semibold text-sm">Audio and listening</h3>
				<dl className="grid grid-cols-2 gap-x-6 gap-y-2 text-sm sm:grid-cols-3">
					{rows.map((row) => (
						<div key={row.label} className="flex flex-col">
							<dt className="text-muted-foreground text-xs">
								{row.label}
							</dt>
							<dd className="tabular">{row.value}</dd>
						</div>
					))}
				</dl>
			</section>

			<section className="flex flex-col gap-3">
				<h3 className="font-semibold text-sm">Cost</h3>
				<dl className="grid grid-cols-3 gap-x-6 gap-y-2 text-sm">
					<div className="flex flex-col">
						<dt className="text-muted-foreground text-xs">Total</dt>
						<dd className="tabular">{usdCost(call.costUsd)}</dd>
					</div>
					<div className="flex flex-col">
						<dt className="text-muted-foreground text-xs">Heard</dt>
						<dd className="tabular">
							{clockDuration(call.sttSeconds)}
						</dd>
					</div>
					<div className="flex flex-col">
						<dt className="text-muted-foreground text-xs">
							Spoken
						</dt>
						<dd className="tabular">
							{clockDuration(call.ttsSeconds)}
						</dd>
					</div>
				</dl>
			</section>

			{terminate ? (
				<section className="flex flex-col gap-3">
					<h3 className="font-semibold text-sm">
						Hang-up as Meta sent it
					</h3>
					<pre className="max-h-64 overflow-auto rounded-2xl bg-muted/60 p-3 font-mono text-xs leading-relaxed">
						{JSON.stringify(terminate.detail ?? {}, null, 2)}
					</pre>
				</section>
			) : null}
		</div>
	);
}

/** The media and listening numbers a call recorded; one it did not record is left out. */
function listeningRows(call: Detail): Array<{ label: string; value: string }> {
	const stats = call.stats;
	const rows: Array<{ label: string; value: string } | null> = [
		typeof stats?.rtpLossPct === "number"
			? { label: "Packet loss", value: `${stats.rtpLossPct.toFixed(1)}%` }
			: null,
		typeof stats?.jitterMs === "number"
			? { label: "Jitter", value: `${Math.round(stats.jitterMs)} ms` }
			: null,
		typeof stats?.rttMs === "number"
			? { label: "Round trip", value: `${Math.round(stats.rttMs)} ms` }
			: null,
		call.firstAudioMsP50 !== null
			? {
					label: "First audio (median)",
					value: `${call.firstAudioMsP50} ms`,
				}
			: null,
		{ label: "Turns", value: String(call.turns) },
		{ label: "Barge-ins", value: String(call.bargeIns) },
		typeof stats?.noiseDropped === "number"
			? { label: "Noise dropped", value: String(stats.noiseDropped) }
			: null,
		typeof stats?.heldTurns === "number"
			? { label: "Held sentences", value: String(stats.heldTurns) }
			: null,
		stats && stats.dtmf.length > 0
			? { label: "Keys pressed", value: stats.dtmf.join(" ") }
			: null,
	];
	return rows.filter((row) => row !== null);
}

function MetaError({ call }: { call: Detail }) {
	return (
		<div className="flex flex-col gap-1 rounded-2xl bg-destructive/8 px-4 py-3 text-sm">
			<span className="font-medium">
				Meta error {call.errorCode}
				{call.errorTitle ? `: ${call.errorTitle}` : ""}
			</span>
			{call.errorDocUrl ? (
				<a
					href={call.errorDocUrl}
					target="_blank"
					rel="noreferrer"
					className="inline-flex w-fit items-center gap-1 text-muted-foreground underline underline-offset-4"
				>
					What Meta says about it
					<ExternalLinkIcon className="size-3" aria-hidden />
				</a>
			) : null}
		</div>
	);
}

function CallIdentifiers({
	call,
	organizationSlug,
}: {
	call: Detail;
	organizationSlug: string;
}) {
	return (
		<section className="flex flex-col gap-3">
			<h3 className="font-semibold text-sm">Identifiers</h3>
			<CopyField
				label="WhatsApp call id (wacid)"
				value={call.externalId}
			/>
			<CopyField label="Call id" value={call.id} />
			{call.conversationId ? (
				<div className="flex items-center justify-between gap-3 text-sm">
					<span className="text-muted-foreground">Conversation</span>
					<a
						href={`/app/${organizationSlug}/conversations?id=${encodeURIComponent(call.conversationId)}`}
						className="truncate font-mono text-xs underline underline-offset-4"
					>
						{call.conversationId}
					</a>
				</div>
			) : null}
			{call.voiceInstance ? (
				<div className="flex items-center justify-between gap-3 text-sm">
					<span className="text-muted-foreground">Call service</span>
					<span className="truncate font-mono text-xs">
						{call.voiceInstance}
					</span>
				</div>
			) : null}
		</section>
	);
}

function CallTimeline({ events }: { events: Detail["events"] }) {
	const steps = withDeltas(events);
	return (
		<section className="flex flex-col gap-3">
			<h3 className="font-semibold text-sm">Timeline</h3>
			{steps.length === 0 ? (
				<p className="text-muted-foreground text-sm">
					No steps were recorded.
				</p>
			) : (
				<ol
					className={cn(SURFACE, "divide-y divide-border/70 text-sm")}
				>
					{steps.map((step, index) => (
						<li
							// A step is one event at one moment: its time and type name it.
							key={`${step.at}-${step.type}`}
							className="flex items-center gap-3 px-4 py-2"
						>
							<span
								className={cn(
									"min-w-0 flex-1 truncate",
									step.type === "error" && "text-destructive",
								)}
							>
								{EVENT_LABEL[step.type] ?? step.type}
							</span>
							{step.delta !== null && index > 0 ? (
								<span className="text-muted-foreground text-xs tabular">
									+{step.delta.toLocaleString("en-US")} ms
								</span>
							) : null}
							<span className="w-20 shrink-0 text-right font-mono text-xs tabular">
								{step.ms !== null
									? `${step.ms.toLocaleString("en-US")} ms`
									: "-"}
							</span>
						</li>
					))}
				</ol>
			)}
		</section>
	);
}

export function CallDetailSkeleton() {
	return (
		<div className="flex min-h-0 flex-1 flex-col">
			<div className="flex flex-col gap-3 border-border/70 border-b px-4 py-3 md:px-6">
				<Skeleton className="h-6 w-48" />
				<Skeleton className="h-4 w-64" />
				<Skeleton className="h-8 w-full" />
			</div>
			<div className="mx-auto flex w-full max-w-3xl flex-col gap-4 px-4 py-5 md:px-6">
				{[0, 1, 2, 3].map((i) => (
					<div key={i} className="flex gap-3">
						<Skeleton className="h-4 w-10" />
						<div className="flex flex-1 flex-col gap-1.5">
							<Skeleton className="h-3 w-16" />
							<Skeleton className="h-4 w-full" />
						</div>
					</div>
				))}
			</div>
		</div>
	);
}
